import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { WordpressAuthService } from './wordpress-auth.service';

import { DynamicConfigService } from '@app/core/config/dynamic-config.service';
import { Observable, of, switchMap, from } from 'rxjs';

import { environment } from '@env/environment';

@Injectable({ providedIn: 'root' })
export class DictionaryService {
  private wpUrl: string = '';

  constructor(
    private http: HttpClient,
    private auth: WordpressAuthService,
    private configService: DynamicConfigService
  ) {
    // Initialize WordPress URL from dynamic config
    this.initializeConfig();
  }

  private async initializeConfig(): Promise<void> {
    try {
      const wpConfig = await this.configService.getWordpressConfig();
      this.wpUrl = this.resolveWpUrl(wpConfig.wpurl);
      console.log('DictionaryService initialized with wpUrl:', this.wpUrl);
    } catch (error) {
      console.error('Failed to initialize DictionaryService config:', error);
      this.wpUrl = this.resolveWpUrl(environment.fallback_wpurl);
    }
  }

  /** Proxy WordPress requests in local dev to bypass browser CORS. */
  private resolveWpUrl(fullUrl: string): string {
    const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (!isLocalDev) {
      return fullUrl.replace(/\/+$/, '');
    }
    try {
      const parsed = new URL(fullUrl);
      const path = parsed.pathname.replace(/\/+$/, '');
      return `/kdb-proxy${path}`;
    } catch {
      return fullUrl.replace(/\/+$/, '');
    }
  }

  getStores(): Observable<any[]> {
    const token = this.auth.getToken();

    const fetchStores = (jwt: string) => {
      const headers = new HttpHeaders({
        Authorization: `Bearer ${jwt}`,
      });
      const base = this.wpUrl.replace(/\/+$/, '');
      return this.http.get<any[]>(`${base}/wp-json/custom/v1/stores`, {
        headers,
      });
    };

    // Already authenticated
    if (token) {
      return fetchStores(token);
    }

    // Not logged in yet → login first
    return this.auth.login().pipe(switchMap(() => fetchStores(this.auth.getToken()!)));
  }

  lookup(term: string, language = 'en', sources: string[] = []): Observable<any> {
    const doLookup = (token: string) => {
      const headers = new HttpHeaders({
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      });
      const base = this.wpUrl.replace(/\/+$/, '');
      return this.http.post(`${base}/wp-json/custom/v1/lookup`, { term, language, sources }, { headers });
    };

    return this.auth.isAuthenticated()
      ? doLookup(this.auth.getToken()!)
      : this.auth.login().pipe(switchMap(() => doLookup(this.auth.getToken()!)));
  }
}
